/* Bar Fridges Australia — model stars, the product page's top-of-page rating.
 *
 * Real browser source, copied verbatim into assets/bfau-model-stars.js by
 * build-reviews.js, for the same reason model-reviews.browser.js is: escaping a
 * file twice is what put a literal backslash in a regex and turned a $$ into a
 * $, and both only failed in a browser.
 *
 * WHAT IT DOES
 *
 * The rating beside the SKU in the product meta row used to be Feefo's own
 * product-stars widget, which asks about ONE SKU. Feefo keys reviews on variant
 * SKU, so a branded wrap of a plain cabinet gets no stars at all and a plain
 * fridge gets only its own share of the model's reviews. This asks the same
 * question the review section at the foot of the page asks — the pooled figure
 * for the whole chassis — so the two numbers on one page agree.
 *
 * It renders over Feefo's widget rather than replacing it in the markup: their
 * stars are first paint and the fallback, and are hidden only once we have a
 * figure of our own. A failed fetch leaves the shopper the stars they have
 * today, which is the same contract the finder and the review block keep.
 *
 * STARS ARE A MODEL SCORE and carry no attribution — decided 2026-08-19. A
 * rating and a count are an aggregate about the model and read as one. The
 * moment a customer's WORDS appear they are labelled with the fridge that
 * customer bought, which is the review block's job, not this one's.
 *
 * DISPLAY ONLY. None of this may reach structured data — every product page
 * already emits a broken aggregateRating of 0, and these figures are not the fix.
 */
(function () {
  var root = document.querySelector('.bfau-ms');
  if (!root) return;
  var sku = root.getAttribute('data-sku');
  if (!sku) return;

  /* Feefo's own summary endpoint, as the storefront widget builds it.
     since_period=all is load-bearing: omit it and the API answers zero for
     products that plainly have reviews, with no error to notice.
     product_sku takes a COMMA-SEPARATED LIST and Feefo pools it, weighted — the
     pooling is server-side, so this is one request however big the chassis.
     Deliberately duplicated from model-reviews.browser.js rather than shared:
     the two ship as separate assets and either may be installed without the
     other, and a query string with a load-bearing parameter in it is better
     repeated than reached for across files. */

  /* ---------------------------------------------------------------------
     Condition-prefixed stock: scratch-and-dent (SD-) and factory second (T2-).
     Ported from condition() + rootOf() in derive.js -- the browser has to ask
     the same question at runtime that the Node build asks, because these SKUs
     are NOT in the baked map and never will be reliably.

     They churn: a unit is listed when it arrives and gone when it sells, and
     the model code it wraps is different every time. A map keyed on SKU is
     stale for them the day it is built, so the chassis is derived here instead
     and looked up in the `_chassis` map that build-reviews.js emits alongside.

       SD-TW103-BC70B-ROADIE-AUS   scratch and dent, report TW103, of BC70B-ROADIE-AUS
       T2-A645-SC372W-VC           factory second,   report A645,  of SC372W-VC

     The token after the prefix names whoever wrote the damage report and has no
     product meaning, so it has to come off before the chassis is read. But a
     report code (A645, TW103, BA) is the same SHAPE as a model code -- EC68
     looks exactly like TW103 -- so BOTH readings are tried and whichever names
     a real chassis wins. Longest strip first: usually the next token IS the
     report code.
     --------------------------------------------------------------------- */
  var CONDITION = /^(?:SD|T2)-\s*/i;

  /* The chassis a model belongs to, read off the code alone -- same regex as
     rootOf() in derive.js. SC70-B-CCR and HUS-SC70-SS both reduce to their
     chassis; the trailing design code is exactly the part a new livery changes,
     so this keeps working on a SKU the map has never seen. */
  function rootOf(text) {
    var m = String(text || '').toUpperCase().replace(/^(HUS|DW|EC)-/, '');
    var hit = /^(?:([A-Z]+)-)?([A-Z]+\d+)/.exec(m);
    if (!hit) return m;
    return hit[1] ? hit[1] + '-' + hit[2] : hit[2];
  }

  /* A chassis whose review pool splits by finish -- SC70 only, and the same one
     entry as POOL_SPLIT in derive.js. The stainless SC70s are triple glazed and
     the black ones double glazed, so their reviews do not describe each other
     and the map is keyed SC70-B / SC70-SS rather than SC70. Read as a SEGMENT
     right after the model number, so a design code that merely contains the
     letters cannot answer. */
  var SPLIT = { SC70: 1 };
  function finishToken(text) {
    var seg = String(text || '').toUpperCase().replace(/^(HUS|DW|EC)-/, '').split('-');
    var i = -1;
    for (var n = 0; n < seg.length; n++) { if (/^SC70L?$/.test(seg[n])) { i = n; break; } }
    var next = i < 0 ? null : seg[i + 1];
    return next === 'B' || next === 'SS' ? next : null;
  }

  /* Every chassis key this SKU might be filed under, best reading first.

     A plain SKU gives one reading; a condition-prefixed one gives two, because
     the report code cannot be told from a model code by shape -- EC68 looks
     exactly like TW103. Longest strip first: usually the next token IS the
     report code.

     Each reading yields the SPLIT key before the bare root, so a scratch-and-
     dent SC70 lands on the side its finish token puts it rather than failing to
     find a bare "SC70" that the map does not carry. */
  function chassisKeys(sku) {
    var raw = String(sku || '').trim();
    var m = CONDITION.exec(raw);
    var readings = m
      ? [raw.slice(m[0].length).trim().replace(/^[A-Z]+\d*-\s*/i, ''), raw.slice(m[0].length).trim()]
      : [raw];
    var out = [];
    readings.forEach(function (c) {
      if (!c) return;
      var r = rootOf(c);
      if (!r) return;
      if (SPLIT[r]) {
        var t = finishToken(c);
        if (t && out.indexOf(r + '-' + t) < 0) out.push(r + '-' + t);
      }
      if (out.indexOf(r) < 0) out.push(r);
    });
    return out;
  }

  /* The SKU list to ask Feefo about.
     
     1. Exact match on the baked SKU map -- the pool build-reviews.js actually
        computed for this fridge, FUNCTIONAL gate and all. Always wins.
     2. `_solo` names the SKUs the build looked at and deliberately left without
        a pool, because no donor on the chassis is the same machine. Silence is
        the correct answer for those, so they stop here. Without this step they
        are indistinguishable from a SKU the build never saw, and 16 of them --
        SK86R-SD, SK245-SD, SG2H-SD and the like -- would take the chassis pool
        and show a heated-glass fridge's reviews on a solid-door card.
     3. The chassis map. This is what carries condition-prefixed stock (SD-/T2-)
        and any design added since the last build.
     4. Own SKU. Never an EMPTY list: Feefo answers a blank product_sku with the
        MERCHANT-WIDE score, which would put the shop's overall rating on an
        unreviewed fridge. */
  function poolFor(sku, map) {
    if (!sku) return sku;
    if (map[sku]) return map[sku];
    var solo = map._solo || [];
    if (solo.indexOf(sku) >= 0) return sku;
    var byChassis = map._chassis || {};
    var keys = chassisKeys(sku);
    for (var i = 0; i < keys.length; i++) {
      if (byChassis[keys[i]]) return byChassis[keys[i]];
    }
    return sku;
  }

  var Q = '?since_period=all&unanswered_feedback=include'
    + '&merchant_identifier=bar-fridges-australia&origin=bar-fridges-australia.com.au'
    + '&source=on_page_product_integration';
  var SUMMARY = 'https://api.feefo.com/api/10/reviews/summary/product' + Q;

  /* Where the count links to, in order of preference: our own review section,
     then Feefo's widget if that is still what is installed at the foot of the
     page. Whichever is there, the click goes to the reviews — which is what
     Feefo's widget did by setting location.href to
     <product url>#feefo-product-review-widgetId. */
  var TARGETS = ['.bfau-mr', '#feefo-product-review-widgetId'];

  /* The shared star art (star-css.js): a solid SVG star at Feefo's own #FFD000,
     sized in px by the stylesheet rather than by font size. Two numbers, not one
     percentage, because the row has gaps in it — 96% of a five-star row is 4.8
     stars plus a slice of gap, and at 22px that slice is visible. */
  function starStyle(r) {
    var v = Math.max(0, Math.min(5, r || 0));
    var n = Math.floor(v);
    return '--bfau-star-n:' + n + ';--bfau-star-frac:' + (v - n).toFixed(2);
  }

  function stars(r) {
    return '<span class="bfau-ms-stars" style="' + starStyle(r)
      + '" aria-hidden="true"><i></i></span>';
  }

  /* The star levels these figures count, and the ONE thing that must read the
     same on all three surfaces -- the grid card, this rating, and the review
     section at the foot of the page. It is asserted in build-reviews.js, for
     the reason every other invariant here is: three copies of a browser file
     that quietly drift apart fail only on a product page, as a card saying
     (25) above a section showing 22.

     1- and 2-star reviews are not quoted in the section below, so they are not
     counted here either. The SCORE is deliberately still Feefo's, over every
     review including those -- see the note in model-reviews.browser.js. That is
     why the count is not "what the score is based on" and no longer says so. */
  var SHOWN = '3,4,5';
  function isShown(level) { return SHOWN.split(',').indexOf(String(level)) >= 0; }

  /* How many QUOTED reviews sit at each star level. Free — the summary response
     carries 5_star..1_star beside the average — and it is what the hover panel
     shows, the same panel Feefo's widget opens on hover. The levels SHOWN
     excludes are held at 0, so the panel and the section agree bar for bar. */
  var HIST = {};

  function bars(count) {
    var h = '';
    for (var i = 5; i >= 1; i--) {
      var n = HIST[i] || 0;
      var w = count ? (n / count) * 100 : 0;
      h += '<div class="bfau-pop-r"><span class="bfau-pop-l">' + i + ' Star'
        + (i === 1 ? '' : 's') + '</span><span class="bfau-pop-n">' + n + '</span>'
        + '<span class="bfau-pop-bar"><i style="width:' + w.toFixed(1) + '%"></i></span></div>';
    }
    return h;
  }

  /* Appended to <body>, not to the meta row, for the reason Feefo's own widget
     does it: the panel is wider than the rating it hangs off and the product
     column clips. Positioned on open rather than by CSS, because fixed
     coordinates are the only ones that survive the row being anywhere. */
  var pop = null;

  function popover(rating, count) {
    var score = rating % 1 === 0 ? String(rating) : rating.toFixed(1);
    pop = document.createElement('div');
    pop.className = 'bfau-ms-pop';
    pop.setAttribute('role', 'tooltip');
    pop.hidden = true;
    pop.innerHTML = '<p class="bfau-pop-t">' + score + '/5 Stars</p>' + bars(count);
    document.body.appendChild(pop);
  }

  function openPop(anchor) {
    if (!pop) return;
    pop.hidden = false;
    var a = anchor.getBoundingClientRect();
    var w = pop.offsetWidth;
    var left = a.left + a.width / 2 - w / 2;
    /* Kept on screen at both edges — on a phone the rating sits near the left
       margin and a centred panel would hang off it. */
    left = Math.max(8, Math.min(left, document.documentElement.clientWidth - w - 8));
    var below = a.bottom + 10;
    var fits = below + pop.offsetHeight < window.innerHeight - 8;
    pop.style.left = left + 'px';
    pop.style.top = (fits ? below : Math.max(8, a.top - pop.offsetHeight - 10)) + 'px';
  }

  function closePop() { if (pop) pop.hidden = true; }

  function render(rating, count) {
    var score = rating % 1 === 0 ? String(rating) : rating.toFixed(1);
    /* "(202)", the shape Feefo's widget renders — stars then the count in
       brackets — so the row reads as it did yesterday. The score is in the
       accessible name and the tooltip rather than on screen, for the same
       reason: matching what is there. */
    /* Not "based on N reviews" any more: N is how many are quoted below, and
       the score is Feefo's over all of them. Two true facts, no false claim
       that one is computed from the other. */
    var label = score + ' out of 5. ' + count + ' review'
      + (count === 1 ? '' : 's') + ' of this model. Read the reviews.';
    root.innerHTML = '<a class="bfau-ms-link" href="#feefo-product-review-widgetId"'
      + ' title="' + label + '" aria-label="' + label + '">'
      + stars(rating)
      + '<span class="bfau-ms-count">(' + count + ')</span></a>';
    root.hidden = false;

    /* Feefo's stars go now, not before — until this line there was nothing to
       replace them with.
       A class on <html> and an !important rule in the snippet, NOT an inline
       display on their div. Their widget renders on its own schedule, usually
       after this, and writes its own display when it does; an inline style of
       ours loses that race and the shopper gets two ratings side by side. A
       stylesheet rule wins whenever it lands. */
    document.documentElement.classList.add('bfau-ms-on');

    popover(rating, count);
    var link = root.querySelector('.bfau-ms-link');
    if (!link) return;
    link.addEventListener('mouseenter', function () { openPop(link); });
    link.addEventListener('mouseleave', closePop);
    /* Keyboard reaches it too: the panel is the only place the breakdown is. */
    link.addEventListener('focus', function () { openPop(link); });
    link.addEventListener('blur', closePop);
    window.addEventListener('scroll', closePop, { passive: true });
  }

  /* A hash jump lands the reviews under the sticky header, which is what the
     widget's own location.href did. Scrolled by hand instead, clearing the
     sticky bar. Delegated from the container because render() writes the anchor
     after this is bound. */
  root.addEventListener('click', function (e) {
    var a = e.target.closest ? e.target.closest('.bfau-ms-link') : null;
    if (!a) return;
    var el = null;
    for (var i = 0; i < TARGETS.length && !el; i++) el = document.querySelector(TARGETS[i]);
    if (!el) return;                      // no review section on the page; let the hash try
    e.preventDefault();
    /* .t4s-section-header is the element the theme makes position:sticky (see
       sections/header-bottom.liquid) — is-header--stuck sits on <html> and
       measuring THAT would offset by the height of the whole document. */
    var hdr = document.querySelector('.t4s-section-header');
    var off = hdr ? Math.min(hdr.offsetHeight, 160) : 0;
    var top = el.getBoundingClientRect().top + window.pageYOffset - off - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  });

  function start(pool) {
    fetch(SUMMARY + '&product_sku=' + encodeURIComponent(pool), { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) {
        var rt = j && j.rating;
        var pr = (rt && rt.product) || {};
        var rating = (rt && rt.rating) || 0;
        /* The count is summed from the SHOWN levels rather than read from
           pr.count, which is every review Feefo holds. This is the number the
           section at the foot of the page will show, and the two are read off
           the same histogram so they cannot drift. */
        var count = 0;
        for (var i = 1; i <= 5; i++) {
          HIST[i] = isShown(i) ? (pr[i + '_star'] || 0) : 0;
          count += HIST[i];
        }
        /* Nothing quoted anywhere on this chassis — no reviews at all, or none
           above 2 stars: stay hidden and leave Feefo's own widget alone, which
           is the honest fallback, since it draws the full picture. */
        if (!count || !rating) return;
        render(rating, count);
      })
      /* Silent. The shopper keeps the stars Feefo drew. */
      .catch(function () {});
  }

  /* The pool is baked because a product page cannot work it out: it holds one
     product, and Feefo cannot be asked which SKUs share a chassis. The same
     frothy-pools.json the review block reads, so the two cannot disagree — and
     the second read of it is free, being one CDN file already in cache. A
     product the map has never seen resolves through its chassis instead; only
     a fridge whose chassis is unknown too falls back to its own SKU, which is
     what Feefo's widget would have shown anyway. */
  fetch(root.getAttribute('data-pools'), { credentials: 'omit' })
    .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
    .then(function (map) { start(poolFor(sku, map)); })
    .catch(function () { start(sku); });
})();
